import {
  batch,
  ComponentProps,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  JSX,
  Show,
  VoidComponent,
} from "solid-js";
import Input, { ExtendableInputProps } from "./Input";
import SelectMenuWithSearch from "../menu/SelectMenuWithSearch";
import { SelectOption, TextOption } from "../../types/Menu";
import CountriesService, { Countries } from "../../services/CountriesService";
import { useTranslation } from "../../state/i18n";
import { isEmpty, isNil } from "lodash-es";
import Text from "../text/Text";
import { Icon } from "../icon/Icon";
import { DialCode, formatPhoneNumberForStorage, parseStoredPhoneNumber } from "../../utils/phoneNumberUtil";
import { getCountryName } from "../../utils/countryUtil";
import { UseQueryResult } from "@tanstack/solid-query";
import { MoreAppError } from "../../MoreAppError";

type PhoneNumberProps = {
  ariaLabel?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: JSX.FocusEventHandler<HTMLInputElement, FocusEvent>;
  disabled?: boolean;
};

type PhoneNumberInputProps = PhoneNumberProps & ExtendableInputProps;

type BasePhoneNumberInputProps = PhoneNumberProps & {
  id: string;
  errorMessage?: string;
  countriesQuery: UseQueryResult<Countries, MoreAppError>;
};

const BasePhoneNumberInput: VoidComponent<BasePhoneNumberInputProps> = (props) => {
  const { t } = useTranslation();
  const hasError = createMemo(() => !isNil(props.errorMessage) && !isEmpty(props.errorMessage));

  const [dialCode, setDialCode] = createSignal<DialCode>();
  const [number, setNumber] = createSignal("");
  // A number typed before a dial code is known can't be emitted and would be lost
  const isNumberDisabled = () => props.disabled === true || isNil(dialCode());

  createEffect(() => {
    const parsed = parseStoredPhoneNumber(props.value, props.countriesQuery.data?.countries ?? []);
    if (!isNil(parsed)) {
      const currentDialCode = dialCode();
      // Keep the selected country on ambiguous calling codes instead of guessing wrong.
      if (isNil(currentDialCode) || currentDialCode.callingCode !== parsed.callingCode) {
        setDialCode({ alpha2Code: parsed.alpha2Code, callingCode: parsed.callingCode });
      }
      setNumber(parsed.number);
      return;
    }

    // By default set dial code based on IP
    if (isNil(dialCode())) {
      const current = props.countriesQuery.data?.current;
      if (!isNil(current)) {
        setDialCode({ alpha2Code: current.alpha2Code, callingCode: current.callingCode });
      }
    }
  });

  // Only emit on user input, so setting the parsed or default dial code doesn't change the value
  const onChange = (currentDialCode: DialCode | undefined, currentNumber: string) => {
    if (isNil(currentDialCode)) {
      return;
    }
    props.onChange?.(
      isEmpty(currentNumber) ? undefined : formatPhoneNumberForStorage({ ...currentDialCode, number: currentNumber }),
    );
  };

  return (
    // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
    <div class="group flex rounded has-[input:focus-visible]:outline-default">
      <DialCodeInput
        countriesQuery={props.countriesQuery}
        value={dialCode()}
        onSelect={(selectedDialCode) => {
          // Batched so the parse effect doesn't reset the dial code from the value that hasn't been emitted yet
          batch(() => {
            setDialCode(selectedDialCode);
            onChange(selectedDialCode, number());
          });
        }}
        disabled={props.disabled}
      />
      <input
        id={props.id}
        aria-label={props.ariaLabel}
        type="tel"
        class="w-full truncate rounded-e-sm border border-y p-2 text-base-regular text-primary outline-none placeholder:text-base-regular"
        classList={{
          "border-error bg-surface-destructive": hasError(),
          "border-disabled bg-surface-disabled": !hasError() && isNumberDisabled(),
          "border-default bg-surface-default": !hasError() && !isNumberDisabled(),
        }}
        value={number()}
        placeholder={t(($) => $.PHONE_NUMBER_INPUT_PLACEHOLDER)}
        onInput={(e) => {
          setNumber(e.currentTarget.value);
          onChange(dialCode(), e.currentTarget.value);
        }}
        onBlur={props.onBlur}
        disabled={isNumberDisabled()}
      />
    </div>
  );
};

const PhoneNumberInput: VoidComponent<PhoneNumberInputProps> = (props) => {
  const { t } = useTranslation();
  const countriesQuery = CountriesService.getCountries();
  // Without countries there are no dial codes, so explain why the number is disabled
  const errorMessage = () =>
    props.errorMessage ?? (countriesQuery.isError ? t(($) => $.PHONE_NUMBER_INPUT_COUNTRIES_FETCH_FAILED) : undefined);

  return (
    <Input {...props} errorMessage={errorMessage()}>
      {(id) => <BasePhoneNumberInput {...props} id={id} countriesQuery={countriesQuery} />}
    </Input>
  );
};

type DialCodeInputProps = {
  value: DialCode | undefined;
  onSelect: (value: DialCode) => void;
  countriesQuery: UseQueryResult<Countries, MoreAppError>;
  disabled?: boolean;
};

const DialCodeInput: VoidComponent<DialCodeInputProps> = (props) => {
  const id = createUniqueId();
  const { t, currentLanguage } = useTranslation();

  const options = createMemo(() =>
    (props.countriesQuery.data?.countries ?? []).map(
      (country) =>
        ({
          variant: "text",
          label: `${getCountryName(country, currentLanguage())} | +${country.callingCode}`,
          value: { alpha2Code: country.alpha2Code, callingCode: country.callingCode },
        }) satisfies TextOption<DialCode>,
    ),
  );
  const flattenedOptions = createMemo(() => options().map((o) => ({ title: "dialCodes", ...o })));

  const selectedOption = createMemo(() =>
    options().find((option) => option.value.alpha2Code === props.value?.alpha2Code),
  );

  const selectedOptions = createMemo(() => {
    const selected = selectedOption();
    return isNil(selected) ? [] : [selected];
  });

  const triggerValue = createMemo<SelectOption<DialCode> | undefined>(() => {
    const selected = selectedOption();
    return isNil(selected) ? undefined : { ...selected, label: `+${selected.value.callingCode}` };
  });

  return (
    <SelectMenuWithSearch
      id={id}
      ariaLabel={t(($) => $.PHONE_NUMBER_INPUT_COUNTRY_CODE_ARIA)}
      options={{ dialCodes: options() }}
      flattenedOptions={flattenedOptions()}
      selectedOptions={selectedOptions()}
      Trigger={(triggerProps) => (
        <CountryCodeSelectTrigger
          ariaLabel={t(($) => $.PHONE_NUMBER_INPUT_COUNTRY_CODE_ARIA)}
          value={triggerValue()}
          disabled={props.disabled || props.countriesQuery.isLoading}
          arkProps={triggerProps}
        />
      )}
      placeholder={t(($) => $.PHONE_NUMBER_INPUT_COUNTRY_CODE_SEARCH_PLACEHOLDER)}
      width="240px"
      multiple={false}
      onSelect={(option) => props.onSelect(option.value)}
    />
  );
};

export default PhoneNumberInput;

type CountryCodeSelectTriggerProps<T> = {
  ariaLabel: string;
  value: SelectOption<T> | null | undefined;
  arkProps: ComponentProps<"button">;
  disabled: boolean;
};

const CountryCodeSelectTrigger = <T,>(props: CountryCodeSelectTriggerProps<T>) => {
  const { t } = useTranslation();

  return (
    <button
      aria-label={props.ariaLabel}
      class="group relative flex h-9.5 w-20 shrink-0 items-center rounded-l-sm border-y border-l border-default bg-surface-extra-subtle py-2 pr-1 pl-2 text-start hover:bg-surface-extra-subtle-hover focus-visible:outline-default active:bg-surface-extra-subtle-pressed"
      disabled={props.disabled}
      {...props.arkProps}
    >
      <div class="flex w-full min-w-0">
        <Show
          when={props.value}
          fallback={
            <Text
              truncate
              text={t(($) => $.PHONE_NUMBER_INPUT_COUNTRY_CODE_PLACEHOLDER)}
              class="text-base-medium text-secondary select-none"
            />
          }
        >
          {(option) => <Text truncate text={option().label} class="text-base-medium text-secondary select-none" />}
        </Show>
      </div>
      <div class="flex size-4 shrink-0 items-center justify-center">
        <Icon type="solid" name="chevronDown" size="md" class="shrink-0 icon-secondary" />
      </div>
    </button>
  );
};
